import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { shallowEqual } from 'hooks/reduxHooks'
import { rem } from 'polished'
import type { ComponentProps, PropsWithChildren, ReactElement, Ref } from 'react'
import { memo } from 'react'
import styled from 'styled-components'
import TextButton from '../Button/TextButton'
import BodyTextBlock from '../TextBlocks/BodyTextBlock'
import { REBRAND_PALETTE_CLASS } from 'lib/theme/paletteVersion'
import { bannerKindStyles } from './styles'
import type { MessageBannerKind } from './types'

export type { MessageBannerKind }

const BannerSheet = styled.div`
  ${bannerKindStyles}

  body.${REBRAND_PALETTE_CLASS} & {
    border-radius: ${props => props.theme.borderRadius.M};
  }

  body.${REBRAND_PALETTE_CLASS} &.flush-top {
    border-end-start-radius: 0;
    border-end-end-radius: 0;
  }
`

const BannerContainer = styled.div`
  display: grid;

  &.align-start {
    grid-template:
      "content ." auto
      "actions actions" auto / auto 1fr;
  }
  &.align-center {
    grid-template:
      ". content ." auto
      "actions actions actions" auto / 1fr auto 1fr;
  }

  &.density-comfortable {
    padding: ${rem(16)};
  }
  &.density-compact {
    padding: ${rem(12)} ${rem(16)};
  }

  ${mediaQueryUp.tablet} {
    align-items: center;

    &.align-on-tablet-start {
      grid-template: "content . actions" auto / auto 1fr auto;
    }
    &.align-on-tablet-center {
      grid-template: ". content actions ." auto / 1fr auto auto 1fr;
    }
  }
`

const BannerContent = styled(BodyTextBlock)`
  grid-area: content;
`

const BannerActionsGroup = styled(Group)`
  grid-area: actions;
  margin-top: ${rem(8)};

  ${mediaQueryUp.tablet} {
    margin: 0;
    margin-inline-start: ${rem(12)};
  }
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  ref?: Ref<HTMLDivElement>
  /** sets the colour of the banner */
  kind: MessageBannerKind
  /**
   * sets the horizontal alignment of the content
   * @default 'start'
   * */
  align?: 'start' | 'center'
  /**
   * sets the horizontal alignment of the content on tablet breakpoint and higher
   * @default falls back to `align`
   * */
  tabletAlign?: 'start' | 'center'
  /**
   * sets the padding of the banner container
   *
   * @default comfortable
   * */
  density?: 'comfortable' | 'compact'
  /**
   * Squares the bottom corners so the banner can sit flush on top of a rounded
   * parent. Use this when the banner is a full-bleed header strip.
   */
  flush?: 'top'
  /** sets the icon at the start of the description */
  icon?: ReactElement | false
  /** sets the textual content that sits above the description as a heading */
  heading?: string | ReactElement
  className?: string
  id?: string
  /**
   * sets the props passed into the primary action
   *  */
  primaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
  /** Button emphasis for the primary action; defaults to the banner's usual secondary style. */
  primaryActionKind?: 'primary' | 'secondary'
  /**
   * sets the props passed into the secondary action
   *  */
  secondaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
}

function MessageBanner({
  children,
  kind,
  align = 'start',
  tabletAlign,
  density = 'comfortable',
  heading,
  icon,
  className,
  flush,
  primaryActionButtonProps,
  primaryActionKind = 'secondary',
  secondaryActionButtonProps,
  ...rest
}: Props) {
  return <BannerSheet
    {...rest}
    className={clsx(
      className,
      `kind-${kind}`,
      flush === 'top' && 'flush-top',
    )}
  >
    <BannerContainer
      className={clsx(
      `align-${align}`,
      `align-on-tablet-${tabletAlign ?? align}`,
      `density-${density}`,
      )}
    >
      <BannerContent
        startIcon={icon}
        variant="medium"
        align={align}
        tabletAlign={tabletAlign}
      >
        <Group direction="vertical" gap={4}>
          {!!heading && <b>{heading}</b>}
          <div>
            {children}
          </div>
        </Group>
      </BannerContent>

      {(primaryActionButtonProps || secondaryActionButtonProps) && <BannerActionsGroup
        direction="horizontal"
        horizontalAlign="end"
        gap={8}
      >
        {secondaryActionButtonProps && <TextButton {...secondaryActionButtonProps} kind="tertiary" />}
        {primaryActionButtonProps && <TextButton {...primaryActionButtonProps} kind={primaryActionKind} />}
      </BannerActionsGroup>}
    </BannerContainer>
  </BannerSheet>
}

export default memo(MessageBanner, (prevProps, nextProps) => {
  const {
    primaryActionButtonProps: prevPrimaryActionButtonProps,
    secondaryActionButtonProps: prevSecondaryActionButtonProps,
    ...prevRest
  } = prevProps
  const {
    primaryActionButtonProps: nextPrimaryActionButtonProps,
    secondaryActionButtonProps: nextSecondaryActionButtonProps,
    ...nextRest
  } = nextProps

  return shallowEqual(prevRest, nextRest) &&
    shallowEqual(prevPrimaryActionButtonProps, nextPrimaryActionButtonProps) &&
    shallowEqual(prevSecondaryActionButtonProps, nextSecondaryActionButtonProps)
})
